import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  //Grid,
  Pagination,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
//import SearchIcon from "@mui/icons-material/Search";

import AcreedoresApi from "api/acreedoresApi";
import CreateAcreedor from "./createAcreedor";

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function SelectAcreedorModal({ open, onClose, onSelect, onSuccess = () => {} }) {
  const [acreedores, setAcreedores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const searchInputRef = useRef(null);

  // Estados de paginación
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    page_size: 10, // Menos registros por página en modal
    has_next: false,
    has_previous: false,
  });

  // Debounced search term para evitar múltiples calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Función optimizada para obtener acreedores con paginación
  const fetchAcreedores = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        setError(null);

        const requestParams = {
          page: params.page || pagination?.current_page || 1,
          page_size: params.page_size || pagination?.page_size || 10,
          search: debouncedSearchTerm,
          //es_recurrente: true, // Solo acreedores recurrentes en el modal
          ...params,
        };

        const response = await AcreedoresApi.fetchAcreedoresPaginacion(requestParams);

        setAcreedores(response.data.results);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("Error al cargar acreedores:", error);
        setError(obtenerMensajeError(error, "Error al cargar los acreedores"));
        setAcreedores([]);
        setPagination((prev) => ({ ...(prev || {}), total_items: 0, total_pages: 0 }));
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearchTerm]
  );

  // Efecto para cargar datos cuando cambia la búsqueda
  useEffect(() => {
    if (open && !showCreateForm) {
      setPagination((prev) => ({ ...(prev || {}), current_page: 1 }));
      fetchAcreedores({ page: 1 });
    }
  }, [debouncedSearchTerm, open, showCreateForm, fetchAcreedores]);

  // Efecto para carga inicial del modal
  useEffect(() => {
    if (open && !showCreateForm) {
      setInitialLoading(true);
      fetchAcreedores({ page: 1 });

      // Focus en el input de búsqueda cuando se abre el modal
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    // Limpiar el término de búsqueda cuando se cierra el modal
    if (!open) {
      setSearchTerm("");
      setPagination({
        current_page: 1,
        total_pages: 0,
        total_items: 0,
        page_size: 10,
        has_next: false,
        has_previous: false,
      });
      setAcreedores([]);
    }
  }, [open, showCreateForm]);

  // Función para cambiar de página
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, current_page: newPage }));
    fetchAcreedores({ page: newPage });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }
    setSearchTerm("");
    onClose();
  };

  const handleAcreedorSelect = (acreedor) => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    onSelect(acreedor);
    setSearchTerm("");
    onClose();
  };

  const handleCreateAcreedorClick = () => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    setShowCreateForm(true);
  };

  const handleCreateFormClose = () => {
    setShowCreateForm(false);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      aria-labelledby="select-acreedor-title"
    >
      <DialogTitle id="select-acreedor-title" sx={{ mb: -3 }}>
        <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={6}>
            <SuiTypography variant="h6" component="div">
              {showCreateForm ? "Nuevo Acreedor" : "Buscar Acreedor"}
            </SuiTypography>
          </Grid>
          <Grid size={6}>
            {!showCreateForm && (
              <SuiTypography variant="h6" component="div">
                {pagination?.total_items || 0} acreedores encontrados
              </SuiTypography>
            )}
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <SuiBox py={3}>
          {showCreateForm ? (
            <CreateAcreedor
              open={showCreateForm}
              onClose={handleCreateFormClose}
              onSelect={handleAcreedorSelect}
              onSuccess={() => {
                fetchAcreedores({ page: 1 });
                if (onSuccess) onSuccess();
              }}
            />
          ) : (
            <>
              <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                <Grid size={9}>
                  <SuiInput
                    inputRef={searchInputRef}
                    placeholder="Buscar por nombre o identificación"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    //startAdornment={<SearchIcon sx={{ color: "text.secondary", mr: 1 }} />}
                    fullWidth
                    autoFocus
                    disabled={loading}
                  />
                </Grid>
                <Grid size={3}>
                  <SuiButton
                    variant="outlined"
                    buttonColor="info"
                    fullWidth
                    onClick={handleCreateAcreedorClick}
                    disabled={loading}
                  >
                    Crear Acreedor
                  </SuiButton>
                </Grid>
              </Grid>
              {/* Loading inicial */}
              {initialLoading && (
                <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={12}>
                    <SuiBox
                      mt={3}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={200}
                    >
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <CircularProgress size={30} />
                        <SuiTypography variant="button" fontWeight="regular" sx={{ mt: 2 }}>
                          Cargando acreedores...
                        </SuiTypography>
                      </Box>
                    </SuiBox>
                  </Grid>
                </Grid>
              )}

              {/* Error */}
              {error && (
                <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={12}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  </Grid>
                </Grid>
              )}

              {/* Paginación */}
              {(pagination?.total_pages || 0) > 1 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={3}
                  pt={2}
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <SuiTypography variant="caption" color="text">
                    Página {pagination?.current_page || 1} de {pagination?.total_pages || 1}
                  </SuiTypography>
                  <Pagination
                    count={pagination?.total_pages || 1}
                    page={pagination?.current_page || 1}
                    onChange={handlePageChange}
                    disabled={loading}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {/* Lista de acreedores */}
              {!initialLoading && (
                <SuiBox mt={3}>
                  <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                    {acreedores.map((acreedor) => (
                      <Grid key={acreedor.id} size={12}>
                        <SuiButton
                          fullWidth
                          variant="outlined"
                          buttonColor="dark"
                          onClick={() => handleAcreedorSelect(acreedor)}
                          disabled={loading}
                          sx={{
                            textAlign: "left",
                            justifyContent: "flex-start",
                            textTransform: "none",
                            mb: 1,
                          }}
                        >
                          <SuiBox>
                            <SuiTypography variant="button" fontWeight="medium">
                              {acreedor.nombre_completo}
                            </SuiTypography>
                            <SuiTypography variant="caption" color="text" display="block">
                              {acreedor.tipo_identificacion}: {acreedor.identificacion} -{" "}
                              {acreedor.tipo || "N/A"}
                            </SuiTypography>
                          </SuiBox>
                        </SuiButton>
                      </Grid>
                    ))}

                    {!initialLoading && !loading && acreedores.length === 0 && (
                      <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                        <Grid size={12}>
                          <SuiBox textAlign="center" py={4}>
                            <SuiTypography variant="button" fontWeight="regular" color="text">
                              No se encontraron acreedores
                            </SuiTypography>
                          </SuiBox>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>

                  {/* Loading discreto para cambios de página */}
                  {loading && !initialLoading && (
                    <SuiBox display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={20} />
                    </SuiBox>
                  )}
                </SuiBox>
              )}
            </>
          )}
        </SuiBox>
      </DialogContent>

      {!showCreateForm && (
        <DialogActions>
          <SuiButton onClick={handleClose} buttonColor="light">
            Cancelar
          </SuiButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

SelectAcreedorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default SelectAcreedorModal;
